import {resolve} from "../../container";
import {LocatedDates} from "../../common/utils/dates/locatedDates";
import {schedule} from "../../common/utils/promises";
import {DefaultFeedSource} from "./feedSource";
import {FeedEntry, type FeedEntryData} from "./feedEntry";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {UnLitElement} from "../../common/elements";
import {type EopViewportSpinner, type Spinner} from "../../page/elements/spinner";
import {Dictionary} from "../../page/elements/dictionary";
import type {Nullable} from "../../bootstrap/common/types";
import {trackingButtonInteract, trackingTeaserInteract} from "../../tracking/trackingAttributes";
import {simpleFeedChannelMap} from "./feedCommons";

@customElement("eop-teaser-sequence")
export class EopTeaserSequence extends UnLitElement {

    @query("eop-spinner")
    protected fetchSpinner: Spinner;
    @query("eop-viewport-spinner")
    protected fetchMoreSpinner: EopViewportSpinner;
    @property({attribute: "number", type: Number})
    private limit: number = 3;
    @property({attribute: "sources"})
    private sources: string;
    @property({attribute: "keywords"})
    private keywords: string;
    @property({attribute: "excluded-keywords"})
    private excludedKeywords: string;
    @property({attribute: "character-limit", type: Number})
    private characterLimit: number = 180;
    @property({attribute: "language"})
    private language: string;
    @property({attribute: "information-density"})
    private informationDensity: string;
    @state()
    private feedEntries: FeedEntry[];

    private fetchMoreAmount: number;
    private dictionary: Dictionary;

    public constructor(
        private feedSource: DefaultFeedSource = new DefaultFeedSource()
    ) {
        super();
        this.feedEntries = [];
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        this.feedSource.configure({
            sources: simpleFeedChannelMap(this.sources?.parseCommaSeparated()),
            keywords: this.keywords?.parseCommaSeparated() ?? [],
            excludedKeywords: this.excludedKeywords?.parseCommaSeparated() ?? [],
            characterLimit: this.characterLimit,
            language: this.language ?? ""
        });
        this.fetchMoreAmount = this.getAttribute("fetch-more-amount")?.toInt() ?? 0;
    }

    public render(): TemplateResult {
        return html`
            <eop-viewport-spinner></eop-viewport-spinner>
            <eop-spinner></eop-spinner>
            <div class="social-teasers">
                ${this.renderTiles()}
            </div>
            ${this.renderFetchMoreButton()}
        `;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        void schedule(this.fetchSpinner.spinWhile(this.fetchInitial()))
            .as("fetch");
    }

    private renderTiles(): Nullable<TemplateResult[]> {
        if (this.feedEntries.isEmpty()) {
            return null;
        }
        return this.feedEntries.map(entry => html`
            <eop-teaser-sequence-tile .entry=${entry} class=${this.informationDensity}></eop-teaser-sequence-tile>`
        );
    }

    private renderFetchMoreButton(): Nullable<TemplateResult> {
        const fetchMoreLabel = this.dictionary.translate("MSG_TEASER_FETCH_MORE");
        return this.showFetchMore() ? html`
            <div class="fetch-more-button">
                <button
                        class="primary"
                        type="button"
                        ${trackingButtonInteract(fetchMoreLabel)}
                        @click=${this.fetchMoreHandler}
                >
                    ${fetchMoreLabel}
                </button>
            </div>` : null;
    }

    private showFetchMore(): boolean {
        return this.fetchMoreAmount > 0
            && this.feedSource.total() > this.feedEntries.length;
    }

    private fetchMoreHandler(): void {
        const offset = this.feedEntries.length;
        void schedule(this.fetchMoreSpinner.spinWhile(this.fetchMore(offset)))
            .as("fetch-more");
    }

    private async fetchInitial(): Promise<void> {
        this.feedEntries = await this.fetchTiles(this.limit, 0);
    }

    private async fetchMore(offset: number): Promise<void> {
        const tiles = await this.fetchTiles(this.fetchMoreAmount, offset);
        this.feedEntries = this.feedEntries.slice(0, offset).concat(tiles);
    }

    private async fetchTiles(size: number, offset: number): Promise<FeedEntry[]> {
        const entries = await this.feedSource.fetch(size, offset);
        return entries.map(entry => {
            return FeedEntry.fromResponse(entry)
                .inChannelContext(this.feedSource.channels());
        });
    }
}

@customElement("eop-teaser-sequence-tile")
export class EopTeaserSequenceTile extends UnLitElement {

    @property()
    public entry: FeedEntry;

    private data: FeedEntryData;
    private dictionary: Dictionary;

    public constructor(
        private dates: LocatedDates = resolve(LocatedDates)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        this.data = this.entry.data;
    }

    public render(): TemplateResult {
        const trackingLabel = this.data.headline ?? "no-headline";
        const readMoreLabel = this.dictionary.translate("MSG_TEASER_SEQUENCE_READ_MORE");
        const ariaLabel = readMoreLabel + ": " + this.data.headline;
        return html`
            <div class="social-teaser" ${trackingTeaserInteract(trackingLabel, this.data.url)}>
                <a href=${this.data.url} class="teaser-image" aria-hidden="true" tabindex="-1">
                    ${this.imageElement()}
                </a>
                <a href=${this.data.url} class="teaser-text reverse-background" aria-label=${ariaLabel}>
                    <div class="tile-top">
                        <div class="metadata">${this.getMetaData()}</div>
                    </div>
                    <div class="teaser-headline">${unsafeHTML(this.data.headline) ?? ""}</div>
                    <div class="teaser-description">${this.data.subHeadline ?? ""}</div>
                    <div class="more-link">
                        <span class="link common-link">${readMoreLabel}</span>
                    </div>
                </a>
            </div>`;
    }

    private imageElement(): TemplateResult {
        return html`
            <eop-responsive-image image-src=${this.entry.getImageUrlFor7x4Purpose()} image-alt=${this.data.imageAltText}></eop-responsive-image>`;
    }

    private getMetaData(): string {
        const date = this.dates.toLocalDateString(new Date(this.data.modifiedDate));
        if (this.data.principalKeyword) {
            return `${date} | ${this.data.principalKeyword}`;
        } else {
            return `${date}`;
        }
    }
}